import { useSyncExternalStore } from "react";
import {
  unstable_serialize,
  type Cache,
  type Key,
  type Middleware,
  type State,
} from "swr";

/**
 * The cache backing every `useSWR` in the app, installed via `<SWRConfig>` in
 * `RootProviders`. In-memory by default: entries live and die with the tab.
 *
 * Keys passed to `createSWRCache` are the exception — their data is mirrored to
 * `localStorage` and restored on the next load, so a returning tab paints from
 * cache while its revalidation is still in flight. Which keys those are is a
 * decision for the composition root, not for this module.
 *
 * Replacing SWR's module-global cache has one consequence worth knowing: the
 * bare `mutate` exported from "swr" is bound to that global cache and no longer
 * reaches this one. Always take `mutate` from `useSWRConfig()`.
 */

const STORAGE_PREFIX = "funtico:swr:";

/** Build-time prerendering runs this module in Node — see adr/023. */
const isBrowser = typeof window !== "undefined";

type CacheEntry = State<unknown, unknown> & { _k?: unknown };

function read(storageKey: string): unknown {
  if (!isBrowser) return undefined;
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/** A `null` payload is stored as absence — it is what sign-out writes. */
function write(storageKey: string, data: unknown): void {
  if (!isBrowser) return;
  try {
    if (data === null) {
      window.localStorage.removeItem(storageKey);
    } else {
      window.localStorage.setItem(storageKey, JSON.stringify(data));
    }
  } catch {
    // Quota exceeded or a non-serializable payload. Dropping the write is
    // correct: the cache is an optimization, never the source of truth.
  }
}

/** The `localStorage` key a persisted SWR key is mirrored to. */
export function persistedStorageKey(key: Key): string {
  return STORAGE_PREFIX + unstable_serialize(key);
}

export function createSWRCache(persistedKeys: Key[] = []): Cache {
  const map = new Map<string, CacheEntry>();
  const persisted = new Map<string, string>();

  for (const key of persistedKeys) {
    const serialized = unstable_serialize(key);
    const storageKey = persistedStorageKey(key);
    persisted.set(serialized, storageKey);

    const data = read(storageKey);
    // `_k` is the key as callers write it; SWR stores it alongside the
    // serialized one, and filter-based `mutate` matches on it.
    if (data !== undefined) map.set(serialized, { data, _k: key });
  }

  if (persisted.size === 0) return map;

  return {
    keys: () => map.keys(),
    get: (key) => map.get(key),
    set: (key, value) => {
      map.set(key, value as CacheEntry);

      const storageKey = persisted.get(key);
      if (!storageKey) return;

      const { data, error } = value as CacheEntry;
      // Never overwrite a good snapshot with a failure, and skip the partial
      // updates SWR merges in as `isValidating` flips — only `null` (a known
      // empty, i.e. signed out) should clear what is stored.
      if (error !== undefined || data === undefined) return;

      write(storageKey, data);
    },
    delete: (key) => {
      map.delete(key);

      const storageKey = persisted.get(key);
      if (storageKey) write(storageKey, null);
    },
  };
}

const subscribeNever = () => () => {};

/**
 * The middleware that makes restored entries safe to hydrate. Install it
 * with the same keys as `createSWRCache`.
 *
 * Restored data comes from this browser's storage, which the prerendered HTML
 * never saw — the build renders every persisted key as not yet known. Reading
 * it during hydration made the first client render disagree with that HTML
 * (React #418), and React threw the whole prerendered tree away to rebuild it.
 *
 * So while hydrating, those keys read as the build saw them, and React
 * re-renders with the restored data straight after — a normal update, not a
 * mismatch. `getServerSnapshot` is only consulted during hydration, so a
 * component mounted later (a client navigation) gets the data on its first
 * render.
 */
export function hideRestoredUntilHydrated(persistedKeys: Key[]): Middleware {
  // Only keys that actually had a snapshot in storage when the app started can
  // make hydration disagree with the prerendered HTML. For the rest — every
  // anonymous visitor — the server snapshot is already the client's, so no
  // subscriber pays a re-render right after hydration.
  const restored = new Set(
    persistedKeys
      .map((key) => unstable_serialize(key))
      .filter((serialized) => read(STORAGE_PREFIX + serialized) !== undefined)
  );

  return (useSWRNext) => (key, fetcher, config) => {
    const isGated = restored.has(unstable_serialize(key));
    const isHydrated = useSyncExternalStore(
      subscribeNever,
      () => true,
      () => !isGated
    );
    const swr = useSWRNext(key, fetcher, config);

    if (isHydrated) return swr;

    // SWR's response tracks which fields a component reads through getters,
    // so shadow `data` alone instead of spreading (which would read them all).
    const beforeHydration: typeof swr = Object.create(swr, {
      data: { value: undefined },
    });
    return beforeHydration;
  };
}
